/**
 * Central Schema.org / JSON-LD helpers for AEO (Answer Engine Optimization).
 *
 * These builders keep the JSON-LD blobs consistent across Landing, FAQ,
 * and Blog surfaces so LLM answer engines (ChatGPT / Perplexity / Gemini /
 * Google SGE) can extract facts confidently and cite the specific route.
 *
 * Everything is a plain-object builder — the callers wrap the output in
 * <script type="application/ld+json"> either via `<Helmet>` or via a
 * direct `dangerouslySetInnerHTML` script tag.
 */

const SITE = "https://cameraclubscore.com";
const LOGO = `${SITE}/icon-512.png`;
const OG = `${SITE}/og-image.png`;

/** Organization — used on every public page. Includes sameAs so AI
 * engines can verify entity identity + author attribution. */
export function orgSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Camera Club Score",
    url: SITE,
    logo: LOGO,
    email: "mail@cameraclubscore.com",
    description:
      "Photo competition management software for UK camera clubs — live scoring, PDI & print support, season league tables, PDI software import, printable PDFs.",
    foundingDate: "2025",
    areaServed: { "@type": "Country", name: "United Kingdom" },
    knowsAbout: [
      "Photo competition scoring",
      "Camera club management",
      "PDI (projected digital image) competitions",
      "Print competitions",
      "Photographer of the Year rankings",
      "PDI software results import",
      "PAGB and RPS federation battles",
    ],
    sameAs: [
      // Add real profile URLs here as they come online — LinkedIn,
      // Twitter/X, Facebook page, GitHub, etc. Keeping the list empty
      // (rather than fake) is fine; AEO engines penalise fake sameAs.
    ],
  };
}

/**
 * SoftwareApplication + Offer schema for the pricing block. Answer
 * engines quote these when asked "how much is Camera Club Score?" and
 * Google Search shows them as software rich results.
 *
 * Why SoftwareApplication (not Product)? Camera Club Score is a SaaS,
 * not a physical or e-commerce product. Google's Search Console
 * previously flagged Product-schema-derived warnings for missing
 * "Merchant listing" fields (shippingDetails, hasMerchantReturnPolicy)
 * which don't apply to a digital service. SoftwareApplication is the
 * schema.org type Google explicitly recommends for SaaS pricing rich
 * results — the merchant-listing check no longer applies while every
 * useful field (name, offers, category, description) still counts.
 *
 * The `priceValidUntil` / `validFrom` window rolls each pageload so
 * pricing snippets never look stale to search engines.
 */
export function productOffersSchema() {
  const now = new Date();
  const validFrom = now.toISOString().slice(0, 10);
  const nextYear = new Date(now);
  nextYear.setFullYear(nextYear.getFullYear() + 1);
  const validUntil = nextYear.toISOString().slice(0, 10);

  // For a SaaS / digital service, "returns" are handled by cancel-anytime
  // access rather than physical returns. Google accepts a
  // MerchantReturnPolicy with `returnPolicyCategory:
  // MerchantReturnNotPermitted` for downloads / SaaS, provided the
  // customer-service contact is available (email + trial period). This
  // clears the "hasMerchantReturnPolicy" non-critical warning.
  const digitalReturnPolicy = {
    "@type": "MerchantReturnPolicy",
    applicableCountry: "GB",
    returnPolicyCategory:
      "https://schema.org/MerchantReturnNotPermitted",
    merchantReturnLink: `${SITE}/terms`,
  };

  const buildOffer = (offer) => ({
    "@type": "Offer",
    ...offer,
    priceCurrency: "GBP",
    priceValidUntil: validUntil,
    validFrom,
    url: `${SITE}/register`,
    availability: "https://schema.org/InStock",
    seller: { "@type": "Organization", name: "Camera Club Score" },
    hasMerchantReturnPolicy: digitalReturnPolicy,
  });

  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Camera Club Score",
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Photo Competition Manager",
    operatingSystem: "Web",
    description:
      "Photo competition management software for UK camera clubs. Live scoring at competitions, season league tables, PDI software import, printable results PDFs.",
    image: OG,
    url: `${SITE}/#pricing`,
    inLanguage: "en-GB",
    // aggregateRating / review deliberately omitted — inventing them
    // to satisfy the non-critical Search Console warning would violate
    // Google's structured-data guidelines. When real testimonials
    // exist, aggregateRating can be added here.
    offers: [
      buildOffer({
        name: "Starter",
        price: "60",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "60",
          priceCurrency: "GBP",
          unitText: "year",
          referenceQuantity: {
            "@type": "QuantitativeValue",
            value: 20,
            unitText: "photographers",
          },
        },
      }),
      buildOffer({
        name: "Unlimited",
        price: "80",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "80",
          priceCurrency: "GBP",
          unitText: "year",
        },
      }),
    ],
  };
}

/**
 * FAQPage JSON-LD from a list of {q, a} objects. Answers are plain text
 * (not JSX) — callers pass rendered strings so search / AI engines get
 * the exact phrasing they'll show as a citation.
 */
export function faqSchema(faqItems) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: {
        "@type": "Answer",
        text: a,
      },
    })),
  };
}

/**
 * BlogPosting schema (richer than Article — used on individual posts).
 * Includes `speakable` so voice assistants + AI answer engines know
 * which selectors carry the "answer-shaped" content.
 */
export function blogPostingSchema(post) {
  const iso = (post.publish_at || post.date) + "T09:00:00+00:00";
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    datePublished: iso,
    dateModified: iso,
    author: { "@type": "Person", name: post.author, url: SITE },
    publisher: {
      "@type": "Organization",
      name: "Camera Club Score",
      logo: { "@type": "ImageObject", url: LOGO },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${SITE}/blog/${post.slug}`,
    },
    image: `${SITE}/og/blog-${post.slug}.png`,
    keywords: post.tag,
    wordCount: (post.read_minutes || 5) * 200,
    articleSection: post.tag,
    inLanguage: "en-GB",
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [".lead", "h2"],
    },
  };
}

export function jsonLdScript(payload) {
  // Escaped closing script tag guard — /</script/i in a stringified body
  // would break the surrounding <script> element otherwise.
  return JSON.stringify(payload).replace(/</g, "\\u003c");
}
